import _ from 'lodash';
import React from 'react';
import {
  safeAddReduce,
  safeAdd,
  safeMult,
  safeSubstract,
  formatDollarOrEmptyString,
  safeAddNoFixed,
  safeSubstractNoFixed,
  showDollarAmountWithDollar,
  safeDivide,
  convertDateForDisplay,
  generateTimeId,
} from 'common/utils';
import {
  MODES,
  PAYMENT_PATH_ROOT,
  VALUE_CHANGE_FORMAT,
  VALUE_CHANGE_DISPLAY,
  CHANGES_TYPES,
  PRICE,
  QUANTITY,
  ANNUAL_BENEFITS,
  ANNUAL_SALARY,
  ACTUAL_ALLOWED_TOTAL,
  SCOPE_OF_WORK_TOTAL,
  DISALLOWED_TOTAL,
  ALLOWED_EXCESS_TOTAL,
  FED_SHARE_AWARD_COST_NON_MA_AND_MA,
  TOT_AWARD_COST_NON_MA_AND_MA,
  FED_SHARE_COST_NON_MA_AND_MA,
  TOT_AWARD_COST,
  TOT_PROJECT_COST,
  ACTUAL_QUANTITY,
  ACTUAL_PRICE,
  ACTUAL_TOTAL,
} from 'common/requestDetails/constants';
import {BudgetUtils} from 'app-ui-componentlibrary/dist/go/components/common/BudgetUtils';
import {calcTotalsByExcludingManagementCosts} from 'common/requestDetails/budgetCalculation';
import moment from 'moment';
import {PERFORMING_ORG_ID_PATH, COST} from 'common/requestDetails/constants';

const COST_ATTRIBUTE = 'cost';
const LOCAL_AGENCY = 'Local Agency';
const STATE_AGENCY = 'State Agency';
const INVESTMENT = 'investment';
const MANAGEMENTCOSTS = 'managementCosts';

export const calculateCostTotal = (cost) => {
  return safeMult(cost.price, cost.quantity);
};

export const calculateActualCostTotal = (cost) => {
  return safeMult(cost.actualPrice, cost.actualQuantity);
};
export const calculateActualAllowedCostTotal = (cost) => {
  return safeAdd(cost.scopeOfWorkTotal, cost.allowedExcessTotal);
};
export const calculateCategoryTotal = (cost, categoryFieldName) => {
  let result = _.get(cost, categoryFieldName, 0);
  return result;
};

export const calculateUnitCosts = (costArray) => {
  return _.reduce(
    _.map(costArray, (cost) => cost.price),
    (acc, nextPrice) => {
      return safeAdd(acc, nextPrice);
    }
  );
};

export const calculateActualUnitCosts = (costArray) => {
  return _.reduce(
    _.map(costArray, (cost) => cost.actualPrice),
    (acc, nextPrice) => {
      return safeAdd(acc, nextPrice);
    }
  );
};

export const calculateItemCostTotal = (lineItem) => {
  let costs = _.values(lineItem.cost);
  if (_.get(lineItem, 'additionalInformation.ineligible')) {
    return 0;
  }

  return _.reduce(
    _.flatMap(costs),
    (acc, nextCost) => {
      return safeAdd(acc, calculateCostTotal(nextCost));
    },
    0
  );
};

export const calculateItemCostTotalByIncrement = (lineItem, increments) => {
  let total = new Array(increments).fill(0);

  if (_.get(lineItem, 'additionalInformation.ineligible')) {
    return total;
  }

  for (let i = 0; i < increments; i++) {
    let costs = _.values(lineItem.cost[i]);
    total[i] = _.reduce(
      _.flatMap(costs),
      (acc, nextCost) => {
        return safeAdd(acc, calculateCostTotal(nextCost));
      },
      0
    );
  }
  return total;
};

export const calculateItemActualCostTotal = (lineItem) => {
  let costs = _.values(lineItem.cost);
  if (_.get(lineItem, 'additionalInformation.ineligible')) {
    return 0;
  }

  let actualCostArray = _.get(lineItem, 'additionalInformation.actualTotal');
  if (actualCostArray && _.size(actualCostArray) > 0) {
    return _.reduce(
      _.flatMap(actualCostArray),
      (acc, actualTotal) => {
        return safeAdd(acc, actualTotal);
      },
      0
    );
  }

  return _.reduce(
    _.flatMap(costs),
    (acc, nextCost) => {
      return safeAdd(acc, calculateActualCostTotal(nextCost));
    },
    0
  );
};

export const calculateItemCategorizedCostTotal = (
  lineItem,
  categoryFieldName,
  inScopeCategory
) => {
  let costs = _.values(lineItem.cost);
  if (_.get(lineItem, 'additionalInformation.ineligible')) {
    return 0;
  }

  if (!_.get(lineItem, 'additionalInformation.notFullyInScope', false)) {
    if (!inScopeCategory) return 0;
    else return calculateItemActualCostTotal(lineItem);
  }

  let actualCostArray = _.get(lineItem, `additionalInformation.${categoryFieldName}`);
  if (actualCostArray && _.size(actualCostArray) > 0) {
    return _.reduce(
      _.flatMap(actualCostArray),
      (acc, total) => {
        return safeAdd(acc, total);
      },
      0
    );
  }

  return _.reduce(
    _.flatMap(costs),
    (acc, nextCost) => {
      if (ACTUAL_ALLOWED_TOTAL === categoryFieldName)
        return safeAdd(acc, calculateActualAllowedCostTotal(nextCost));
      else return safeAdd(acc, calculateCategoryTotal(nextCost, categoryFieldName));
    },
    0
  );
};

export const calculateItemActualCostTotalByIncrement = (lineItem, increments) => {
  let total = new Array(increments).fill(0);

  if (_.get(lineItem, 'additionalInformation.ineligible')) {
    return total;
  }

  let actualCostArray = _.get(lineItem, 'additionalInformation.actualTotal');
  if (actualCostArray && _.size(actualCostArray) > 0) {
    return actualCostArray;
  }

  if (lineItem.cost && _.isArray(lineItem.cost)) {
    for (let i = 0; i < increments; i++) {
      let costs = _.values(lineItem.cost[i]);
      total[i] = _.reduce(
        _.flatMap(costs),
        (acc, nextCost) => {
          return safeAdd(acc, calculateActualCostTotal(nextCost));
        },
        0
      );
    }
  }
  return total;
};

export const calculateItemActualAllowedCostTotalByIncrement = (lineItem, increments) => {
  let total = new Array(increments).fill(0);

  let actualCostArray = _.get(lineItem, 'additionalInformation.actualAllowedTotal');
  if (actualCostArray && _.size(actualCostArray) > 0) {
    return actualCostArray;
  }

  if (lineItem.cost && _.isArray(lineItem.cost)) {
    for (let i = 0; i < increments; i++) {
      let costs = _.values(lineItem.cost[i]);
      total[i] = _.reduce(
        _.flatMap(costs),
        (acc, nextCost) => {
          return safeAdd(acc, calculateActualAllowedCostTotal(nextCost));
        },
        0
      );
    }
  }
  return total;
};

export const calculateItemCostTotalNotDeferred = (lineItem) => {
  let costs = _.values(lineItem.cost);
  if (
    _.get(lineItem, 'additionalInformation.ineligible') ||
    _.get(lineItem, 'additionalInformation.deferred')
  ) {
    return 0;
  }

  return _.reduce(
    _.flatMap(costs),
    (acc, nextCost) => {
      return safeAdd(acc, calculateCostTotal(nextCost));
    },
    0
  );
};

export const calculateItemCostTotalNotDeferredByIncrement = (lineItem, increments) => {
  let total = new Array(increments).fill(0);

  if (
    _.get(lineItem, 'additionalInformation.ineligible') ||
    _.get(lineItem, 'additionalInformation.deferred')
  ) {
    return total;
  }

  for (let i = 0; i < increments; i++) {
    let costs = _.values(lineItem.cost[i]);
    total[i] = _.reduce(
      _.flatMap(costs),
      (acc, nextCost) => {
        return safeAdd(acc, calculateCostTotal(nextCost));
      },
      0
    );
  }
  return total;
};

export const calculateItemTotalPayment = (lineItem, isPaymentHiring) => {
  let payment = _.get(lineItem, PAYMENT_PATH_ROOT, []);
  let total = 0;
  _.forEach(payment, (paymentInstance) => {
    total += isPaymentHiring
      ? _.get(paymentInstance, 'price', 0)
      : safeMult(
          _.get(paymentInstance, 'quantity', 0),
          _.get(paymentInstance, 'price', 0)
        );
  });
  return total;
};

export const calculateChildrenActualTotalByMode = (children, mode) => {
  const itemCalculator = MODE_TO_ITEM_ACTUAL_CALCULATOR[mode];
  return _.reduce(
    children,
    (acc, item) => {
      if (item.children) {
        return safeAdd(acc, calculateChildrenActualTotalByMode(item.children, mode));
      } else {
        return safeAdd(acc, itemCalculator(item));
      }
    },
    0
  );
};

export const calculateAllItemsCategorizedCostTotal = (data, categoryFieldName) => {
  return safeAddReduce(
    _.map(data, (stream) => {
      return calculateChildrenItemCategorizedTotalByMode(
        stream.children,
        categoryFieldName
      );
    }),
    0
  );
};

export const calculateChildrenItemCategorizedTotalByMode = (
  children,
  categoryFieldName,
  mode = MODES.CLOSEOUT_REPORT_REVIEW
) => {
  return _.reduce(
    children,
    (acc, item) => {
      if (item.children) {
        return safeAdd(
          acc,
          calculateChildrenItemCategorizedTotalByMode(
            item.children,
            categoryFieldName,
            mode
          )
        );
      } else {
        let inScopeCategory = false;
        if (
          categoryFieldName === ACTUAL_ALLOWED_TOTAL ||
          categoryFieldName === SCOPE_OF_WORK_TOTAL
        )
          inScopeCategory = true;
        return safeAdd(
          acc,
          calculateItemCategorizedCostTotal(item, categoryFieldName, inScopeCategory)
        );
      }
    },
    0
  );
};

export const calculateChildrenActualAllowedTotalByMode = (children, mode) => {
  return _.reduce(
    children,
    (acc, item) => {
      if (item.children) {
        return safeAdd(
          acc,
          calculateChildrenActualAllowedTotalByMode(item.children, mode)
        );
      } else {
        return safeAdd(
          acc,
          calculateItemCategorizedCostTotal(item, ACTUAL_ALLOWED_TOTAL, true)
        );
      }
    },
    0
  );
};

export const calculateManagementCostActualAllowedTotal = (fundingStreams) => {
  return safeAddReduce(
    _.map(fundingStreams, (stream) => {
      return calculateChildrenActualAllowedTotalManagementCosts(stream.children);
    }),
    0
  );
};

export const calculateChildrenActualAllowedTotalManagementCosts = (
  children,
  parentItemCode
) => {
  return _.reduce(
    children,
    (acc, item) => {
      const itemCode = _.get(item, 'itemCode');
      if (parentItemCode && parentItemCode != 419) {
        return acc;
      }
      if (item.children) {
        return safeAdd(
          acc,
          calculateChildrenActualAllowedTotalManagementCosts(
            item.children,
            parentItemCode ? parentItemCode : itemCode
          )
        );
      } else {
        return safeAdd(
          acc,
          calculateItemCategorizedCostTotal(item, ACTUAL_ALLOWED_TOTAL, true)
        );
      }
    },
    0
  );
};

export const calculateChildrenTotalByMode = (children, mode, isPaymentHiring) => {
  const itemCalculator = MODE_TO_ITEM_CALCULATOR[mode];
  return _.reduce(
    children,
    (acc, item) => {
      if (item.children) {
        return safeAdd(acc, calculateChildrenTotalByMode(item.children, mode));
      } else {
        return safeAdd(acc, itemCalculator(item, isPaymentHiring));
      }
    },
    0
  );
};

export const calculateChildrenTotalByModeAndIncrements = (
  children,
  mode,
  increments,
  total
) => {
  const itemCalculator = MODE_TO_ITEM_CALCULATOR_BY_INCREMENT[mode];
  if (!total) total = new Array(increments).fill(0);

  const safeArrSum = (arr1, arr2, increments) => {
    let result = new Array(increments).fill(0);
    for (let i = 0; i < increments; i++) {
      result[i] = safeAddNoFixed(arr1[i], arr2[i]);
    }
    return result;
  };

  return _.reduce(
    children,
    (acc, item) => {
      if (item.children) {
        return calculateChildrenTotalByModeAndIncrements(
          item.children,
          mode,
          increments,
          acc
        );
      } else {
        return safeArrSum(acc, itemCalculator(item, increments), increments);
      }
    },
    total
  );
};

const MODE_TO_ITEM_CALCULATOR = {
  [MODES.CLOSEOUT_REPORT]: calculateItemCostTotal,
  [MODES.CLOSEOUT_REPORT_REVIEW]: calculateItemCostTotal,
  [MODES.REVIEW]: calculateItemCostTotalNotDeferred,
  [MODES.PAYMENT_REQUEST]: calculateItemTotalPayment,
  [MODES.APPLICATION]: calculateItemCostTotal,
};

const MODE_TO_ITEM_ACTUAL_CALCULATOR = {
  [MODES.CLOSEOUT_REPORT]: calculateItemActualCostTotal,
  [MODES.CLOSEOUT_REPORT_REVIEW]: calculateItemActualCostTotal,
};

const MODE_TO_ITEM_CALCULATOR_BY_INCREMENT = {
  [MODES.CLOSEOUT_REPORT]: calculateItemActualCostTotalByIncrement,
  [MODES.CLOSEOUT_REPORT_REVIEW]: calculateItemActualAllowedCostTotalByIncrement,
  [MODES.APPLICATION]: calculateItemCostTotalByIncrement,
  [MODES.REVIEW]: calculateItemCostTotalNotDeferredByIncrement,
};

export const createChangeObj = (
  type,
  field,
  originalVal,
  currVal,
  displayText,
  currentlyCollaborating
) => {
  return {
    type,
    field,
    previousValue: originalVal,
    currValue: currVal,
    formatType: VALUE_CHANGE_FORMAT[field] || 'text',
    displayText: displayText || VALUE_CHANGE_DISPLAY[field],
    ...(currentlyCollaborating && {
      id:
        type && _.isEqual(type, CHANGES_TYPES.NEWLY_CREATED)
          ? type
          : field
          ? field
          : generateTimeId(),
    }),
  };
};

export const filterConfig = (childrenConfigs, itemCodeMapping) => {
  let predicate = (config) => {
    let maxOccurs = _.get(config, 'maxOccurs');
    if (_.get(config, 'ineligible')) {
      return false;
    }
    return (
      maxOccurs === undefined || maxOccurs > _.get(itemCodeMapping, config.itemCode, 0)
    );
  };
  return _.filter(childrenConfigs, predicate);
};
export const toItemCode = (itemCode) => {
  return itemCode ? _.toNumber(itemCode) : undefined;
};

export const diffItem = (
  item,
  original,
  labels,
  itemCodeMapping,
  currentlyCollaborating
) => {
  let changes = [];
  let itemToReturn = item;
  let ineligiblePath = 'additionalInformation.ineligible';
  let deferredPath = 'additionalInformation.deferred';

  if (_.get(item, ineligiblePath)) {
    changes.push({
      type: CHANGES_TYPES.INELIGIBLE_CHANGE,
      displayText: `${_.upperFirst(_.get(labels, 'itemLabel', 'Item'))}`,
      becameIneligible: true,
      ...(currentlyCollaborating && {id: CHANGES_TYPES.INELIGIBLE_CHANGE}),
    });
    if (!!original) {
      itemToReturn = {
        ...item,
        additionalInformation: {
          ..._.get(item, 'additionalInformation', {}),
          ineligible: _.get(itemToReturn, 'additionalInformation.ineligible'),
          justification: _.get(itemToReturn, 'additionalInformation.justification'),
        },
      };
    }
  }
  if (_.get(item, deferredPath)) {
    changes.push({
      type: CHANGES_TYPES.DEFERRED_CHANGE,
      displayText: `${_.upperFirst(_.get(labels, 'itemLabel', 'Item'))}`,
      becameDeferred: true,
      ...(currentlyCollaborating && {id: CHANGES_TYPES.DEFERRED_CHANGE}),
    });
    if (!!original) {
      itemToReturn = {
        ...item,
        additionalInformation: {
          ..._.get(item, 'additionalInformation', {}),
          deferred: _.get(itemToReturn, 'additionalInformation.deferred'),
          justification: _.get(itemToReturn, 'additionalInformation.justification'),
        },
      };
    }
  }
  if (!original) {
    changes.push({
      type: CHANGES_TYPES.NEWLY_CREATED,
      displayText: `${_.upperFirst(_.get(labels, 'itemLabel', 'Item'))}`,
      ...(currentlyCollaborating && {id: CHANGES_TYPES.NEWLY_CREATED}),
    });
  } else if (!_.isEqual(_.get(original, 'itemCode'), _.get(itemToReturn, 'itemCode'))) {
    const originalItemName = _.get(
      _.find(
        itemCodeMapping,
        (itemCodeOption) => itemCodeOption.value == original.itemCode
      ),
      'text',
      ''
    );
    const currentItemName = _.get(
      _.find(
        itemCodeMapping,
        (itemCodeOption) => itemCodeOption.value == itemToReturn.itemCode
      ),
      'text',
      ''
    );
    changes.push(
      createChangeObj(
        CHANGES_TYPES.VALUE_CHANGE,
        'itemCode',
        originalItemName,
        currentItemName,
        `${_.upperFirst(_.get(labels, 'itemLabel', 'Item'))}`,
        currentlyCollaborating
      )
    );
  }

  _.set(itemToReturn, 'additionalInformation.changes', changes);
  return itemToReturn;
};

export const checkChildrenForEdits = (children) => {
  let edited = false;
  _.forEach(children, (child) => {
    let changes = _.get(child, 'additionalInformation.changes');
    if (changes && !_.isEmpty(changes)) {
      edited = true;
    } else if (_.get(child, 'children')) {
      edited = checkChildrenForEdits(child.children);
    }
    if (edited) return false;
  });
  return edited;
};

export const filterChangesForEditLabel = (changes) => {
  let changeForEdit = null;
  _.forEach(changes, (change) => {
    if (change.type === CHANGES_TYPES.NEWLY_CREATED) {
      changeForEdit = change;
      return false;
    }
    if (!changeForEdit || changeForEdit.type !== CHANGES_TYPES.DEFERRED_CHANGE) {
      changeForEdit = change;
    }
  });
  return changeForEdit;
};

export const getEditLabelFromEdits = (changes) => {
  let changeForEdit = filterChangesForEditLabel(changes);
  if (changeForEdit === null) {
    return null;
  }
  if (changeForEdit.type === CHANGES_TYPES.NEWLY_CREATED) {
    return 'Added';
  } else if (changeForEdit.type === CHANGES_TYPES.DEFERRED_CHANGE) {
    return 'Deferred';
  } else {
    return 'Edited';
  }
};

export const requestDetailsChangesToDisplayString = (change) => {
  const {type} = change;
  let message = <span />;
  const formatValue = (value, formatType) => {
    if (formatType === 'boolean') {
      return <strong>{value ? 'True' : 'False'}</strong>;
    } else {
      return (
        <strong>
          {formatType === 'dollar'
            ? `${formatDollarOrEmptyString(value !== undefined ? value : '')}`
            : value}
        </strong>
      );
    }
  };

  switch (type) {
    case CHANGES_TYPES.VALUE_CHANGE:
      const prev = formatValue(change.previousValue, change.formatType);
      const curr = formatValue(change.currValue, change.formatType);
      message = (
        <span>
          <strong>{change.displayText}</strong>
          {' from '}
          {prev}
          {' to '}
          {curr}
        </span>
      );
      break;
    case CHANGES_TYPES.INELIGIBLE_CHANGE:
      message = (
        <span>
          <strong>{change.displayText || 'Item'}</strong>
          {' marked '}
          <strong>{change.becameIneligible ? 'ineligible' : 'eligible'}</strong>
        </span>
      );
      break;
    case CHANGES_TYPES.NEWLY_CREATED:
      message = <strong>{change.displayText || 'Item'} created</strong>;
      break;
    case CHANGES_TYPES.VALUE_CHANGE_NO_DISPLAY:
      message = (
        <strong>
          {change.displayText}
          {' changed'}
        </strong>
      );
      break;
    case CHANGES_TYPES.DEFERRED_CHANGE:
      message = (
        <span>
          <strong>{change.displayText || 'Item'}</strong>
          {' marked '}
          <strong>{change.becameDeferred ? 'deferred' : 'not deferred'}</strong>
        </span>
      );
      break;
    default:
      break;
  }

  return message;
};

export const getChildrenMapping = (items) => {
  return _.map(items, (item) => ({
    key: String(item.itemCode),
    value: String(item.itemCode),
    text: item.name,
  }));
};

export const calculatePeriod = (itemConfig) => {
  const {
    effectiveTimePeriodEnd: end,
    effectiveTimePeriodStart: start,
    effectiveTimePeriodType: type,
  } = itemConfig;
  switch (type) {
    case 'year':
      return {
        increments: moment(end).diff(moment(start), type, true),
        type,
      };
    case 'whole':
      return {
        increments: 1,
        type,
      };
    default:
      return {increments: 0, type};
  }
};

export const transformTotalCostToArray = (increments, total, mode, updatedData) => {
  let initialArray = [];
  if (increments === 1) {
    initialArray.push(total);
    return initialArray;
  } else {
    let costArray = _.reduce(
      updatedData,
      (acc, stream) => {
        return calculateChildrenTotalByModeAndIncrements(
          stream.children,
          mode,
          increments,
          acc
        );
      },
      initialArray
    );
    return costArray;
  }
};

export const calculateBudget = (
  budgetCalculationMode,
  periodTerm,
  total,
  fedShareRate,
  appShareRate,
  mode,
  updatedData,
  isCostShareApproved,
  previousGrantBudget
) => {
  let increments = periodTerm ? periodTerm.increments : 1;
  let budget = {
    appShareRate: appShareRate,
    fedShareRate: fedShareRate,
    appShare: [],
    fedShare: [],
    totalShare: transformTotalCostToArray(increments, total, mode, updatedData),
  };

  let calculatedTotalAppShare = 0;

  for (let i = 0; i < increments; i++) {
    //Calculate app share

    let calculatedAppRate = budget.appShareRate[i];
    if (budgetCalculationMode && budgetCalculationMode.deriveAppRate === true) {
      calculatedAppRate = safeDivide(
        budget.appShareRate[i],
        safeAddNoFixed(1, budget.appShareRate[i])
      );
    }

    let periodTotal = _.multiply(budget.totalShare[i], calculatedAppRate);
    let roundedPeriodTotal = undefined;
    if (budgetCalculationMode && budgetCalculationMode.roundUp === true) {
      roundedPeriodTotal = roundUp(periodTotal, 2);
    } else if (
      budgetCalculationMode &&
      budgetCalculationMode.roundToNearestCent === true
    ) {
      roundedPeriodTotal = roundToTheNearestCent(periodTotal);
    } else {
      roundedPeriodTotal = _.floor(periodTotal, 2);
    }
    budget.appShare[i] = _.toNumber(roundedPeriodTotal.toFixed(2));
    calculatedTotalAppShare = safeAddNoFixed(calculatedTotalAppShare, budget.appShare[i]);

    //Reconciliate With Total if applicable
    if (
      budgetCalculationMode &&
      budgetCalculationMode.defaultAppRate &&
      budgetCalculationMode.reconciliateWithTotal === true &&
      i === increments - 1
    ) {
      let calculatedDefaultAppRate = budgetCalculationMode.defaultAppRate;
      if (budgetCalculationMode && budgetCalculationMode.deriveAppRate === true) {
        calculatedDefaultAppRate = safeDivide(
          budgetCalculationMode.defaultAppRate,
          safeAddNoFixed(1, budgetCalculationMode.defaultAppRate)
        );
      }
      let expectedTotalAppShare = roundUp(_.multiply(total, calculatedDefaultAppRate));
      if (calculatedTotalAppShare !== expectedTotalAppShare) {
        let reconciliationAmount = safeSubstractNoFixed(
          expectedTotalAppShare,
          calculatedTotalAppShare
        );
        budget.appShare[i] = safeAddNoFixed(budget.appShare[i], reconciliationAmount);
      }
    }

    //Calculate fedShare
    budget.fedShare[i] = safeSubstractNoFixed(budget.totalShare[i], budget.appShare[i]);
  }

  //updates budget if it has a cost share waiver
  budget = updateBudgetBasedOnWaiver(
    budget,
    increments,
    previousGrantBudget,
    isCostShareApproved
  );

  //Convert to string for backwards compatibility
  if (!budgetCalculationMode || budgetCalculationMode.storeBudgetAsString) {
    budget.appShare = arrOfString(budget.appShare);
    budget.fedShare = arrOfString(budget.fedShare);
  }
  return budget;
};

export const updateBudgetBasedOnWaiver = (
  budget,
  increments,
  previousGrantBudget,
  isCostShareApproved
) => {
  if (isCostShareApproved) {
    const actualTotal = safeAddReduce(budget.totalShare);
    const previousTotal = safeAddReduce(previousGrantBudget.budgetSummary.totalCharges);

    if (actualTotal >= previousTotal) {
      budget.fedShare = previousGrantBudget.share.fedShare;
      for (let i = 0; i < increments; i++) {
        const excessAppShare = safeSubstract(budget.totalShare[i], budget.fedShare[i]);
        budget.appShare[i] = safeAdd(
          previousGrantBudget.share.appShare[i],
          excessAppShare
        );
      }
    } else {
      budget.appShare = previousGrantBudget.share.appShare;
      for (let i = 0; i < increments; i++) {
        budget.fedShare[i] = safeSubstract(budget.totalShare[i], budget.appShare[i]);
      }
    }
  }
  return budget;
};

// Temporarily moving this method here from BudgetUtils until componentLibrary is fixed
const INDIRECT_CHARGES_BOCS = ['indirectCharges'];
export const computeBudgetSummary = (
  bocs,
  fundingStreams,
  numPeriods,
  constructionProgramIncome,
  managementCostItemCodes,
  programIncome,
  foHandleProgIncome,
  calcTpc,
  costCalculation
) => {
  const directChargeBocs = _.difference(bocs, INDIRECT_CHARGES_BOCS);
  const itemBocCharges = BudgetUtils.collectCosts(_.fill(Array(numPeriods), []), {
    children: fundingStreams,
  });
  const bocTotals = sumBocTotals(
    numPeriods,
    bocs,
    itemBocCharges,
    constructionProgramIncome,
    costCalculation
  );

  let totalsByPeriod = BudgetUtils.sumChargesByPeriod(numPeriods, bocTotals, bocs);

  let incomeTotal;
  if (_.isArray(programIncome)) {
    incomeTotal = 0;
    for (let i = 0; i < programIncome.length; i++) {
      incomeTotal = incomeTotal + _.toNumber(programIncome[i]);
    }
  }
  if (
    !(foHandleProgIncome == null) &&
    !(programIncome == null) &&
    (programIncome > 0 || incomeTotal > 0) &&
    calcTpc
  ) {
    const updatedTotalsByPeriod = BudgetUtils.getUpdatedTotalsByPeriod(
      totalsByPeriod,
      foHandleProgIncome,
      programIncome
    );
    totalsByPeriod = updatedTotalsByPeriod;
  }

  const directChargesByPeriod = BudgetUtils.sumChargesByPeriod(
    numPeriods,
    bocTotals,
    directChargeBocs
  );
  const indirectChargesByPeriod = BudgetUtils.sumChargesByPeriod(
    numPeriods,
    bocTotals,
    INDIRECT_CHARGES_BOCS
  );
  let budgetSummary = {
    ...bocTotals,
    totalDirectCharges: directChargesByPeriod,
    totalIndirectCharges: indirectChargesByPeriod,
    totalCharges: totalsByPeriod,
  };

  if (!_.isEmpty(managementCostItemCodes)) {
    budgetSummary = {
      ...budgetSummary,
      totalManagementCosts: sumTotalManagementCosts(
        fundingStreams,
        managementCostItemCodes,
        numPeriods,
        costCalculation
      ),
    };
  }

  return budgetSummary;
};

export const sumBocTotals = (
  numPeriods,
  bocs,
  totals,
  constructionProgramIncome,
  costCalculation
) => {
  const initBocTotals = _.reduce(
    bocs,
    (acc, boc) => _.set(acc, boc, _.fill(Array(numPeriods), 0)),
    {}
  );

  // let quantityValue = QUANTITY;
  // let priceValue = PRICE;

  // if ( costCalculation == "actualAllowedCost")
  //   quantityValue = ACTUAL_A

  const bocTotals = _.reduce(
    totals,
    (acc, periodTotal, period) => {
      _.forEach(periodTotal, (cost) => {
        if (_.includes(bocs, _.get(cost, 'boc'))) {
          const path = `${cost.boc}.${period}`;
          if (costCalculation == 'actualAllowedCost') {
            _.set(
              acc,
              path,
              safeAdd(
                _.get(acc, path),
                safeSubstract(_.get(cost, ACTUAL_TOTAL), _.get(cost, DISALLOWED_TOTAL))
              )
            );
          } else {
            _.set(
              acc,
              path,
              safeAdd(
                _.get(acc, path),
                costCalculation == 'actualCost'
                  ? safeMult(_.get(cost, ACTUAL_QUANTITY), _.get(cost, ACTUAL_PRICE))
                  : safeMult(_.get(cost, QUANTITY), _.get(cost, PRICE))
              )
            );
          }
        }
      });
      return acc;
    },
    initBocTotals
  );

  if (!_.isEmpty(_.get(bocTotals, 'construction')) && constructionProgramIncome) {
    let constructionTotal = safeSubstract(
      _.get(bocTotals, 'construction.0'),
      constructionProgramIncome
    );
    constructionTotal = constructionTotal < 0 ? 0 : constructionTotal;
    bocTotals.construction[0] = constructionTotal;
  }

  return bocTotals;
};

// Temporarily moving this method here from BudgetUtils until componentLibrary is fixed
export const sumTotalManagementCosts = (
  fundingStreams,
  managementCostItemCodes,
  numPeriods,
  costCalculation
) => {
  let totalManagementCosts = _.fill(Array(numPeriods), 0);
  _.forEach(fundingStreams, (child) => {
    const children = _.get(child, 'children', {});
    _.forEach(children, (item) => {
      if (_.includes(managementCostItemCodes, item.itemCode)) {
        let collectedCosts = BudgetUtils.collectCosts(
          _.fill(Array(numPeriods), []),
          item
        );
        _.forEach(collectedCosts, (costs, index) => {
          totalManagementCosts[index] = _.reduce(
            costs,
            (acc, cost) => {
              return safeAdd(
                acc,
                costCalculation
                  ? safeMult(_.get(cost, ACTUAL_QUANTITY), _.get(cost, ACTUAL_PRICE))
                  : safeMult(_.get(cost, QUANTITY), _.get(cost, PRICE))
              );
            },
            totalManagementCosts[index]
          );
        });
      }
    });
  });
  return totalManagementCosts;
};

export const arrOfString = (arr) => {
  let result = [];
  for (let i = 0; i < arr.length; i++) {
    result.push(String(arr[i].toFixed(2)));
  }
  return result;
};
export const deriveAppShareRates = (fedShareRate, periodTerm) => {
  let appShareRate = [];
  let increments = periodTerm ? periodTerm.increments : 1;
  for (let i = 0; i < increments; i++) {
    appShareRate[i] = safeSubstractNoFixed(1, fedShareRate[i]);
  }

  return appShareRate;
};

export const sortFundingStreams = (streams, streamsConfig) => {
  const orderedTypes = _.map(
    streamsConfig,
    (streamConfig) => streamConfig.fundingStreamType
  );
  let sorted = [];
  _.forEach(orderedTypes, (type) => {
    const currStream = _.find(streams, (stream) => stream.fundingStreamType === type);
    if (currStream) {
      sorted.push(currStream);
    }
  });

  return sorted;
};

export const getErrorForField = (validationErrors, fieldName) => {
  return _.get(
    _.find(
      _.get(validationErrors, 'error'),
      (err) => _.get(err, fieldName) !== undefined
    ),
    fieldName
  );
};

const parseError = (error) => {
  if (_.isArray(error)) {
    return _.find(error, (err) => err.isValid === false);
  } else {
    return error;
  }
};

const toError = (firstError, validationDirtyMap, validationDirtyPath) => {
  let showError = !_.get(firstError, 'isValid', true);
  const message = _.get(firstError, 'message', '');
  showError =
    showError && (!validationDirtyPath || _.get(validationDirtyMap, validationDirtyPath));
  return {
    displayError: showError,
    errorMessages: message,
  };
};

export const displayCostError = (
  fieldName,
  validationErrors,
  validationDirtyMap,
  costYear,
  costIndex
) => {
  // search through errors for the one about this field
  const fieldError = getErrorForField(validationErrors, fieldName);
  const costIndexError = _.get(fieldError, [costYear, costIndex]);
  let firstError = parseError(costIndexError);
  return toError(
    firstError,
    validationDirtyMap,
    `cost.${costYear}.${costIndex}.${fieldName}`
  );
};

export const pageLinkRouter = (programId, subGrant, grant) => {
  if (programId === 14 || programId === 15) {
    return `/subGrant/${subGrant}`;
  } else {
    return `/grant/${grant}`;
  }
};

export const findByRosterId = (obj, result, rosterId) => {
  if (_.includes(_.keys(obj), rosterId)) {
    _.set(result, rosterId, _.get(obj, rosterId));
  } else {
    _.each(_.keys(obj), (e) => {
      if (_.isObject(_.get(obj, e))) {
        findByRosterId(_.get(obj, e), result, rosterId);
      }
    });
  }
  return result;
};

export const displayError = (
  fieldName,
  validationDirtyPath,
  validationErrors,
  validationDirtyMap
) => {
  // search through errors for the one about this field
  const fieldError = getErrorForField(validationErrors, fieldName);
  let firstError = parseError(fieldError);
  return toError(firstError, validationDirtyMap, validationDirtyPath);
};

export const displayEmployeeError = (
  fieldName,
  validationDirtyPath,
  validationErrors,
  validationDirtyMap,
  rosterId
) => {
  const employeeErrors = findByRosterId(_.get(validationErrors, 'error'), {}, rosterId);
  const fieldError = _.get(employeeErrors, `${rosterId}.${fieldName}`);
  let firstError = parseError(fieldError);
  return toError(firstError, validationDirtyMap, validationDirtyPath);
};

export const calculateLineItemPaymentTotals = (payments, sourcePath) => {
  const otherPaymentItems = getPastPaymentsForLineItem(payments, sourcePath);

  return _.reduce(
    otherPaymentItems,
    (acc, paymentItem) => {
      return _.reduce(
        paymentItem,
        (internalAcc, payment) => {
          return internalAcc + safeMult(_.get(payment, PRICE), _.get(payment, QUANTITY));
        },
        acc
      );
    },
    0
  );
};

export const getPastPaymentsForLineItem = (payments, sourcePath) => {
  return _.map(payments, (payment) => {
    const otherItem = _.get(payment, `payload.paymentStreams.${sourcePath}`);
    // Decorate with payment request dates
    return _.map(_.get(otherItem, ['additionalInformation', 'payment']), (p) => {
      return {
        ...p,
        request: _.get(payment, 'payload.request'),
      };
    });
  });
};

// Trimming the end
export const roundDown = (p, pos = 2) => {
  const pArray = _.split(_.toString(p), '.');
  return _.size(pArray) > 1
    ? parseFloat(_.join([_.first(pArray), _.last(pArray).substr(0, pos)], '.'))
    : parseFloat(_.join(pArray, '.'));
};

export const roundUp = (p, pos = 2) => {
  const pArray = _.split(_.toString(p), '.');
  let toReturn = parseFloat(_.join(pArray, '.'));
  if (_.size(pArray) > 1) {
    const lastOne = _.parseInt(_.last(pArray).substr(0, pos));
    const lastOnePlusOne = lastOne + 1;
    // In case rounding up last number, ex. 9, or 99, or 999
    let comparator = '1';
    for (let i = 0; i < pos; i++) {
      comparator = comparator + '0';
    }
    comparator = _.parseInt(comparator);
    if (lastOnePlusOne === comparator) {
      toReturn = parseFloat(_.parseInt(_.first(pArray).replace('.', '')) + 1);
    } else {
      toReturn = parseFloat(_.join([_.first(pArray), _.toString(lastOnePlusOne)], '.'));
    }
  }
  return toReturn;
};

export const roundToTheNearestCent = (p) => {
  const formattedNumber = safeMult(p, 1000);
  const truncatedFormattedNumber = Math.trunc(formattedNumber);
  const str = truncatedFormattedNumber.toString();
  const numberAtTheThounsands = _.toNumber(str.charAt(str.length - 1));

  const centsAmount = _.toNumber(str.substring(0, str.length - 1));
  const roundedNumber = safeDivide(centsAmount, 100);

  let paddedAmount = roundedNumber;
  if (numberAtTheThounsands >= 5) {
    paddedAmount = safeAdd(roundedNumber, 0.01);
  }

  return _.toNumber(paddedAmount.toFixed(2));
};

export const calculateFederalTotalForPosition = (props) => {
  const {
    employees,
    item,
    positionId,
    endDate,
    periodOfPerformance,
    grantBudgetShareFedRate: fedShareRates = [],
    approvedPositionPayments = [],
    turnOffvalidationLimits,
  } = props;

  const paymentSection = _.get(item, [...PAYMENT_PATH_ROOT, positionId]);
  // Calculate total for passed in props position
  const calculatedPositionTotal = _.reduce(
    _.map(_.get(paymentSection, 'employees'), (e, key) => {
      // Only add for empoyees in period
      if (
        _.get(
          _.find(employees, (e) => e.rosterId === key),
          'duringPaymentPeriod'
        )
      ) {
        return safeAdd(_.get(e, ANNUAL_BENEFITS), _.get(e, ANNUAL_SALARY));
      }
    }),
    (total, item) => {
      return safeAdd(total, item);
    },
    0
  );

  let requestedFederalShare,
    fedShareRate,
    fedResourcesRequested,
    requestedFedShareIsLessThanRemaining = true,
    toReturn = {};

  if (endDate && periodOfPerformance) {
    const POPStartDate = _.get(periodOfPerformance, 'startDate');
    const POPEndDate = _.get(periodOfPerformance, 'endDate');
    let years = moment(POPEndDate).add(1, 'day').diff(POPStartDate, 'years');
    let index = -1,
      currentFundingPeriod = {};
    for (let i = 0; i < years; i++) {
      const paymentPeriodStartDate = moment(POPStartDate).add(i, 'years');
      const paymentPeriodEndDate = moment(POPStartDate).add(i + 1, 'years');
      if (
        index === -1 &&
        moment(endDate).isBetween(paymentPeriodStartDate, paymentPeriodEndDate)
      ) {
        index = i;
        currentFundingPeriod.start = paymentPeriodStartDate;
        currentFundingPeriod.end = paymentPeriodEndDate;
      }
    }

    fedShareRate = turnOffvalidationLimits ? 1 : _.get(fedShareRates, [index]);

    requestedFederalShare = safeMult(calculatedPositionTotal, fedShareRate);

    const annualTotalFromGrant = safeMult(
      _.reduce(
        _.get(item, 'cost.0'),
        (total, boc) => {
          return safeAdd(total, _.get(boc, 'price', 0));
        },
        0
      ),
      fedShareRate
    );

    // Amount already with applied Fed share rate
    const totalPayments = _.reduce(
      approvedPositionPayments,
      (total, p) => {
        // Add payments with end date within current FO
        const pastPaymentrequestEndDate = moment(_.get(p, 'request.endDate'));
        if (
          pastPaymentrequestEndDate.isBetween(
            currentFundingPeriod.start,
            currentFundingPeriod.end
          ) &&
          pastPaymentrequestEndDate.isBefore(endDate)
        ) {
          total = safeAddNoFixed(total, _.get(p, 'fedResourcesRequestedUnrounded', 0));
        }
        return total;
      },
      0
    );
    // A: the sum of the stored (unrounded) federal resources requested for this position for the current payment
    // and all previous payments from this funding year with status="Approved"
    const valueA = safeAddNoFixed(totalPayments, requestedFederalShare);
    // B: the position cost limit: federal share rate for this funding year times (salary + benefits) awarded per position
    // from the request details, then round down
    const valueB = roundDown(annualTotalFromGrant);

    // GMMGO-7679: Rule 1 decision
    // If A<B, then store the true (unrounded) federal resources requested as the federal resources requested for this position
    // If A>=B, then store the true (unrounded) remaining amount to reach the position cost limit as the federal resources requested for this position

    if (valueA < valueB || valueB === 0 || turnOffvalidationLimits) {
      fedResourcesRequested = requestedFederalShare;
    } else {
      requestedFedShareIsLessThanRemaining = false;
      fedResourcesRequested = safeSubstractNoFixed(
        roundDown(annualTotalFromGrant),
        totalPayments
      );
    }
    fedResourcesRequested = fedResourcesRequested < 0 ? 0 : fedResourcesRequested;
    const returnAmount = _.round(fedResourcesRequested, 2);

    toReturn = {
      fedResourcesRequestedUnrounded: fedResourcesRequested,
      fedShareTotal: showDollarAmountWithDollar(returnAmount),
      fedShareTotalNumber: returnAmount,
      totalPayments: totalPayments,
      requestedIsLessThanRemaining: requestedFedShareIsLessThanRemaining,
      isFedShare100Percent: turnOffvalidationLimits
        ? true
        : parseFloat(fedShareRate) === 1,
    };
  }
  return toReturn;
};

const getItemHackSaferHiring2018 = (staffingBundledDataStream) =>
  _.chain(staffingBundledDataStream)
    .get('children')
    .values()
    .get([0, 'children'])
    .values()
    .get(0)
    .value();

const getItemForPosition = (staffingBundledDataStream, position) =>
  _.get(
    [staffingBundledDataStream],
    position.awardedId,
    getItemHackSaferHiring2018(staffingBundledDataStream)
  );

export const calculateFederalTotal = (positions, props) => {
  // super hack for SAFER 2018 hiring, change for next fo that uses payments
  const item = getItemHackSaferHiring2018(props.staffingBundledDataStream);

  const grandTotal = _.reduce(
    positions,
    (total, position) => {
      const positionItem = getItemForPosition(props.staffingBundledDataStream, position);
      const result = calculateFederalTotalForPosition({
        employees: position.employees,
        item: positionItem,
        positionId: position.positionId,
        endDate: props.endDate,
        periodOfPerformance: props.periodOfPerformance,
        approvedPositionPayments: _.map(props.approvedPayments, (payment) => {
          const staffingItem = getItemForPosition(payment, position);
          const posPayments = _.get(staffingItem, [
            'additionalInformation',
            'payment',
            position.positionId,
          ]);
          return {...posPayments, request: _.get(payment, 'request', {})};
        }),
        grantBudgetShareFedRate: props.grantBudgetShareFedRate,
        turnOffvalidationLimits: props.turnOffvalidationLimits,
      });
      total.fedShareTotalNumber = safeAddNoFixed(
        total.fedShareTotalNumber,
        result.fedShareTotalNumber
      );
      total.fedResourcesRequestedUnrounded = safeAddNoFixed(
        total.fedResourcesRequestedUnrounded,
        result.fedResourcesRequestedUnrounded
      );
      total.totalPayments = safeAddNoFixed(total.totalPayments, result.totalPayments);
      return total;
    },
    {
      fedShareTotalNumber: 0,
      fedResourcesRequestedUnrounded: 0,
      totalPayments: 0,
    }
  );

  // GMMGO-7679: Rule 3
  const valueA = safeAddNoFixed(
    props.sumPastPaymentAmounts,
    roundUp(grandTotal.fedResourcesRequestedUnrounded)
  );

  const valueB = safeAddNoFixed(
    grandTotal.totalPayments,
    grandTotal.fedResourcesRequestedUnrounded
  );

  let calculatedGrandTotal =
    valueA >= valueB
      ? roundDown(grandTotal.fedResourcesRequestedUnrounded)
      : roundUp(grandTotal.fedResourcesRequestedUnrounded);

  // Manual payment with discrepancy
  const lastApprovedPayment = _.maxBy(props.approvedPayments, (payment) => {
    return payment.entityNumber;
  });
  if (_.get(lastApprovedPayment, 'manualPayment', false)) {
    calculatedGrandTotal = safeSubstractNoFixed(
      calculatedGrandTotal,
      _.get(lastApprovedPayment, 'paymentAdjustment', 0)
    );
  }

  return {
    fedResourcesRequestedRounded: grandTotal.fedShareTotalNumber,
    fedResourcesRequestedUnrounded: grandTotal.fedResourcesRequestedUnrounded,
    totalPayments: grandTotal.totalPayments,
    grandTotal: calculatedGrandTotal,
  };
};

const validEmployee = (emp, startDate, endDate) => {
  // if the employee does not have end date , then they are current employee
  const isCurrentEmp = !emp.endDate;

  const empEnd = moment(convertDateForDisplay(emp.endDate));
  const empStart = moment(convertDateForDisplay(emp.startDate));
  const payEnd = moment(convertDateForDisplay(endDate));
  const payStart = moment(convertDateForDisplay(startDate));

  return (
    empStart.isSameOrBefore(payEnd) && (isCurrentEmp || payStart.isSameOrBefore(empEnd))
  );
};

export const getValidPositions = (positions, start, end) => {
  return _.map(positions, (pos) => {
    return {
      ...pos,
      employees: _.map(pos.employees, (emp) => {
        return {
          ...emp,
          duringPaymentPeriod: validEmployee(emp, start, end),
        };
      }),
    };
  });
};

export const countQuantityOfItem = (lineItem) => {
  let costs = _.values(lineItem.cost);
  if (_.get(lineItem, 'additionalInformation.ineligible')) {
    return 0;
  }

  return _.reduce(
    _.flatMap(costs),
    (acc, nextCost) => {
      return safeAdd(acc, nextCost.quantity);
    },
    0
  );
};

/*
 * Find objects within obj that contain the keyToFind
 */
export const findAllByKey = (obj, keyToFind) => {
  return Object.entries(obj).reduce(
    (acc, [key, value]) =>
      _.get(value, keyToFind) // <= if value object has our key, remember it
        ? acc.concat(value)
        : typeof value === 'object'
        ? acc.concat(findAllByKey(value, keyToFind))
        : acc,
    []
  );
};

/*
 * Iterate through the field configuration objects locating objects with a
 * title and templatedProps.entityDataPath properties as well as new changeType property
 * (default to "valueChange") and return an array of objects containing those values
 * for each field.  We want to skip things like listeners that setup data for other
 * field config objects later in the array, hence locating objects with a title and
 * templateProps.entityDataPath.  The recursive nature of findAllByKey(obj, keyToFind)
 * will locate the object if it is nested within top-level object such as a
 * renderAs attribute of a ShowHide component.  Speaking of which, the same
 * field can be defined more than once with a ShowHide.  For example, subprogramFunding,
 * select list is defined with a 3-element list in one case or a 2-element list
 * in another case.  Only add the same field definition once.
 *
 * [ {fieldName: "fooBar", fieldPath="additionalInformation.fooBar", displayText: "Foo bar", changeType: "valueChange"}, ...]
 */
export const extractChangeParamsFromCfg = (fieldConfigs) => {
  const changeParams = [];
  const changeParamFieldNames = [];
  const titleObjs = findAllByKey(fieldConfigs, 'title');
  const fldNameRegex = /(?<fieldPath>additionalInformation.[A-z0-9.]+)/;
  _.forEach(titleObjs, (titleObj) => {
    const displayText = _.get(titleObj, 'title');
    const entityDataPath = _.get(titleObj, 'templatedProps.entityDataPath');
    const changeType = _.get(titleObj, 'changeType', CHANGES_TYPES.VALUE_CHANGE);
    if (!entityDataPath) return;

    // extract the foo from additionalInformation.foo or additionalInformation.narrative.foo
    const fieldPath = _.get(entityDataPath.match(fldNameRegex), 'groups.fieldPath');
    const fieldName = _.last(_.split(fieldPath, '.'));
    if (!fieldName) return;

    if (!_.includes(changeParamFieldNames, fieldName)) {
      changeParamFieldNames.push(fieldName);
      changeParams.push({fieldName, fieldPath, displayText, changeType});
    }
  });
  return changeParams;
};

/******* Below cost related functions need to be moved to BudgetUtils under app-ui-component library *************/
/********* BEGIN **********/
const sumByFieldPathValueAndSubProgramFunding = (costItems, groupByValueList) => {
  const costItemsByGroupByValue = _.groupBy(costItems, (item) => {
    return _.get(item, 'groupByValue', 'unknown');
  });
  const costItemsByGroupByValueBySubprogramFunding = {};
  _.forEach(_.keys(costItemsByGroupByValue), (groupByValue) => {
    const groupedByArray = costItemsByGroupByValue[groupByValue];
    const subProgGroupBy = _.groupBy(groupedByArray, (item) => {
      let subProgFund = 'NONE';
      switch (_.get(item, 'subProgramFunding', 'unkonwn')) {
        case 'SHSP':
        case 'SHSPForSHSP':
        case 'SHSPForOPSG':
          subProgFund = 'SHSP';
          break;
        case 'UASI':
        case 'UASIForUASI':
          subProgFund = 'UASI';
          break;
        case 'OPSG':
        case 'OPSGForOPSG':
          subProgFund = 'OPSG';
          break;
      }
      return subProgFund;
    });
    _.set(costItemsByGroupByValueBySubprogramFunding, groupByValue, subProgGroupBy);
  });

  const groupByValueTotals = {};
  const grandTotals = {
    SHSP: 0,
    UASI: 0,
    OPSG: 0,
    total: 0,
  };
  _.forEach(groupByValueList, (groupByValue) => {
    const costItemsByGroupByValue =
      costItemsByGroupByValueBySubprogramFunding[groupByValue];
    const totalsObj = {};
    if (!costItemsByGroupByValue) {
      totalsObj['SHSP'] = 0;
      totalsObj['UASI'] = 0;
      totalsObj['OPSG'] = 0;
      totalsObj['total'] = 0;
      _.set(groupByValueTotals, groupByValue, totalsObj);
    } else {
      let groupValueTotal = 0;
      _.forEach(['SHSP', 'UASI', 'OPSG', 'NONE'], (subProgFund) => {
        const costItemsForSubProg = costItemsByGroupByValue[subProgFund];
        const total = _.reduce(
          costItemsForSubProg,
          (acc, costItem) => {
            return safeAdd(acc, _.get(costItem, 'total'));
          },
          0
        );
        _.set(totalsObj, subProgFund, total);
        groupValueTotal = safeAdd(groupValueTotal, total);
      });
      _.set(totalsObj, 'total', groupValueTotal),
        (grandTotals['total'] += groupValueTotal);
      grandTotals['SHSP'] += totalsObj['SHSP'];
      grandTotals['UASI'] += totalsObj['UASI'];
      grandTotals['OPSG'] += totalsObj['OPSG'];

      _.set(groupByValueTotals, groupByValue, totalsObj);
    }
  });
  return {
    groupByValues: groupByValueTotals,
    totalValues: grandTotals,
  };
};

const collectItemsContainingFieldPath = (acc, fieldPath, obj) => {
  if (obj === undefined) return acc;
  if (_.get(obj, fieldPath)) {
    acc = _.concat(acc, obj);
  } else if (obj.children) {
    _.forEach(obj.children, (childObj) => {
      acc = collectItemsContainingFieldPath(acc, fieldPath, childObj);
    });
  } else if (_.isArray(obj)) {
    _.forEach(obj, (childObj) => {
      acc = collectItemsContainingFieldPath(acc, fieldPath, childObj);
    });
  }
  return acc;
};

const determineGroupByValue = (fieldPathValue, additionalPathFieldValue) => {
  let groupByValue;
  if (_.isEqual(fieldPathValue, LOCAL_AGENCY)) {
    groupByValue = LOCAL_AGENCY;
  } else if (_.isEqual(fieldPathValue, STATE_AGENCY)) {
    if (!additionalPathFieldValue || _.isEqual(additionalPathFieldValue, 'unknown')) {
      groupByValue = STATE_AGENCY;
    } else {
      groupByValue = LOCAL_AGENCY;
    }
  }
  return groupByValue;
};
const collectCostItemsInObjectAddFieldPathValue = (
  acc,
  fieldPathValue,
  item,
  additionalPathFieldValue
) => {
  if (item === undefined) return acc;
  _.forEach(_.get(item, COST_ATTRIBUTE), (costs, costIdx) => {
    const itemCost = _.cloneDeep(costs[0]);
    itemCost.itemCode = item.itemCode;
    if (additionalPathFieldValue != undefined) {
      const groupByValue = determineGroupByValue(
        fieldPathValue,
        additionalPathFieldValue
      );
      if (groupByValue) {
        _.set(itemCost, 'groupByValue', groupByValue);
      }
    } else {
      _.set(itemCost, 'groupByValue', fieldPathValue);
    }
    _.set(acc, costIdx, _.concat(acc[costIdx] ? acc[costIdx] : [], itemCost));
  });
  _.forEach(item.children, (childObj) => {
    collectCostItemsInObjectAddFieldPathValue(
      acc,
      fieldPathValue,
      childObj,
      additionalPathFieldValue
    );
  });
};

const collectCostItemsInObjectAddFieldPathValueAndSubProgram = (
  acc,
  fieldPathValue,
  item,
  additionalPathFieldValue
) => {
  if (item === undefined) return acc;

  _.forEach(_.get(item, COST_ATTRIBUTE), (costs, costIdx) => {
    const subProgramFunding = _.get(item, 'additionalInformation.subProgramFunding');

    if (subProgramFunding) {
      const itemCost = _.cloneDeep(costs[0]);
      itemCost.itemCode = item.itemCode;
      itemCost.subProgramFunding = subProgramFunding;
      if (additionalPathFieldValue != undefined) {
        const groupByValue = determineGroupByValue(
          fieldPathValue,
          additionalPathFieldValue
        );
        if (groupByValue) {
          _.set(itemCost, 'groupByValue', groupByValue);
        }
      } else {
        _.set(itemCost, 'groupByValue', fieldPathValue);
      }
      _.set(acc, costIdx, _.concat(acc[costIdx] ? acc[costIdx] : [], itemCost));
    }
  });

  _.forEach(item.children, (childObj) => {
    collectCostItemsInObjectAddFieldPathValueAndSubProgram(
      acc,
      fieldPathValue,
      childObj,
      additionalPathFieldValue
    );
  });
};
const collectCostItemsFromItemsWithFieldPath = (
  fieldPath,
  itemsWithFieldPath,
  includeSubProgramFunding,
  additionalCheckPath
) => {
  let costItems = [];
  let additionalPathFieldValue;
  _.forEach(itemsWithFieldPath, (item) => {
    const groupByFieldValue = _.get(item, fieldPath, 'unknown'); // there should be no "unknowns" in array provided
    if (additionalCheckPath != undefined) {
      additionalPathFieldValue = _.get(item, additionalCheckPath, 'unknown');
    }
    if (includeSubProgramFunding === 'false' || !includeSubProgramFunding) {
      collectCostItemsInObjectAddFieldPathValue(
        costItems,
        groupByFieldValue,
        item,
        additionalPathFieldValue
      );
    } else {
      collectCostItemsInObjectAddFieldPathValueAndSubProgram(
        costItems,
        groupByFieldValue,
        item,
        additionalPathFieldValue
      );
    }
  });
  return _.flattenDepth(costItems, 1);
};

export const computeBudgetSummaryByNamedFieldPath = (
  fieldPath,
  fieldValueList,
  fundingStreams,
  includeSubProgramFunding,
  additionalCheckPath = undefined
) => {
  /*
   * Look through fundingStreams item hierarchy to find those items containing the object path specified
   * by fieldPath and add them to the array/accumulator returning all those items found.
   */
  const itemsWithFieldPath = collectItemsContainingFieldPath(
    [],
    fieldPath,
    fundingStreams
  );
  /*
   * Collect all the cost items found in or underneath the items containing the fieldPath.
   * Add to the cost object ( {boc: ..., quanity: ..., price: ..., total: ...}) a groupByValue property
   * w/ value of fieldPath.  Also add a subProgramFunding property with a normalized value (e.g., SHSPForOPSG to SHSP)
   */
  const costItems = collectCostItemsFromItemsWithFieldPath(
    fieldPath,
    itemsWithFieldPath,
    includeSubProgramFunding,
    additionalCheckPath
  );
  /*
   *  create the budgetSummary object to be returned
   */
  const budgetSummary = sumByFieldPathValueAndSubProgramFunding(
    costItems,
    fieldValueList
  );
  return {
    budgetSummary,
  };
};
const multiGroupBy = (seq, keys) => {
  if (!keys.length) return seq;
  var first = keys[0];
  var rest = keys.slice(1);
  return _.mapValues(_.groupBy(seq, first), function (value) {
    return multiGroupBy(value, rest);
  });
};
const sumSolutionAreaTotals = (totals) => {
  const groupedBySolutionArea = multiGroupBy(totals, [
    'solutionArea',
    'subProgramFunding',
  ]);
  let itemTotals = {};
  return _.reduce(
    groupedBySolutionArea,
    (acc, solutionArea) => {
      _.forEach(solutionArea, (items) => {
        _.forEach(items, (item) => {
          if (_.get(item, 'total')) {
            const path = `${item.subProgramFunding}`;
            _.set(
              acc,
              `${item.subProgramFunding}`,
              safeAdd(_.get(acc, path), item.total)
            );
          }
        });
      });
      return acc;
    },
    itemTotals
  );
};
const sumSubProgramTotals = (totals) => {
  const groupedBySubProgramFunding = _.groupBy(totals, 'subProgramFunding');
  let itemTotals = {};
  return _.reduce(
    groupedBySubProgramFunding,
    (acc, subProgramFunding) => {
      _.forEach(subProgramFunding, (item) => {
        if (_.get(item, 'total')) {
          const path = `${item.subProgramFunding}`;
          _.set(acc, path, safeAdd(_.get(acc, path), item.total));
        }
      });
      return acc;
    },
    itemTotals
  );
};

const collectCostsWithSubProgram = (
  acc,
  obj,
  orgIdPerformingWorkPath,
  appOrgId,
  filterByOrg,
  orgTypePath,
  MOUPath
) => {
  let orgIdPerformingWork = _.get(obj, orgIdPerformingWorkPath);
  let organizationType = _.get(obj, orgTypePath);
  let MOUFlag = _.get(obj, MOUPath);

  if (orgIdPerformingWork && filterByOrg) {
    if (
      organizationType == 'Local Agency' ||
      (organizationType == 'State Agency' && MOUFlag)
    ) {
      _.forEach(obj.children, (child) =>
        collectCostsWithSubProgram(
          acc,
          child,
          orgIdPerformingWorkPath,
          appOrgId,
          filterByOrg,
          orgTypePath,
          MOUPath
        )
      );
    }
  } else {
    _.forEach(_.get(obj, COST_ATTRIBUTE), (costs, cost) => {
      const itemCost = _.cloneDeep(costs[0]);
      if (_.get(obj, 'additionalInformation.subProgramFunding')) {
        itemCost.subProgramFunding = _.get(
          obj,
          'additionalInformation.subProgramFunding'
        );
        _.set(acc, cost, _.concat(acc[cost], itemCost));
      }
    });
    if (obj !== undefined) {
      _.forEach(obj.children, (child) =>
        collectCostsWithSubProgram(
          acc,
          child,
          orgIdPerformingWorkPath,
          appOrgId,
          filterByOrg,
          orgTypePath,
          MOUPath
        )
      );
    }
  }
  return acc;
};

const collectCostsWithSubProgramAndSolutionArea = (
  acc,
  obj,
  orgIdPerformingWorkPath,
  appOrgId,
  filterByOrg,
  orgTypePath,
  MOUPath
) => {
  let orgIdPerformingWork = _.get(obj, orgIdPerformingWorkPath);
  if (orgIdPerformingWork && filterByOrg) {
    if (appOrgId == orgIdPerformingWork) {
      _.forEach(obj.children, (child) =>
        collectCostsWithSubProgramAndSolutionArea(
          acc,
          child,
          orgIdPerformingWorkPath,
          appOrgId,
          filterByOrg,
          orgTypePath,
          MOUPath
        )
      );
    }
  } else {
    _.forEach(_.get(obj, COST_ATTRIBUTE), (costs, cost) => {
      const itemCost = _.cloneDeep(costs[0]);
      if (
        _.get(obj, 'additionalInformation.subProgramFunding') &&
        _.get(obj, 'additionalInformation.solutionArea') ==
          'MANAGEMENT_AND_ADMINISTRATION'
      ) {
        itemCost.subProgramFunding = _.get(
          obj,
          'additionalInformation.subProgramFunding'
        );
        _.set(acc, cost, _.concat(acc[cost], itemCost));
      }
    });
    if (obj !== undefined) {
      _.forEach(obj.children, (child) =>
        collectCostsWithSubProgramAndSolutionArea(
          acc,
          child,
          orgIdPerformingWorkPath,
          appOrgId,
          filterByOrg,
          orgTypePath,
          MOUPath
        )
      );
    }
  }
  return acc;
};
export const computeBudgetSummaryByInvestment = (fundingStreams, orgId) => {
  let matchingFundingStream = _.filter(fundingStreams.children, (stream) => {
    return _.includes(INVESTMENT, stream.fundingStreamType);
  });

  let managementCostFundingStream = _.filter(fundingStreams.children, (stream) => {
    return _.includes(MANAGEMENTCOSTS, stream.fundingStreamType);
  });
  const subProgramPassthroughCharges = _.flattenDepth(
    collectCostsWithSubProgram(
      [],
      matchingFundingStream[0],
      'additionalInformation.orgIdPerformingWork',
      orgId,
      true,
      'additionalInformation.organizationType',
      'additionalInformation.memorandumOfUnderstandingInPlace'
    ),
    1
  );
  const subProgramTotalCharges = _.flattenDepth(
    collectCostsWithSubProgram(
      [],
      matchingFundingStream[0],
      'additionalInformation.orgIdPerformingWork',
      orgId,
      false,
      'additionalInformation.organizationType',
      'additionalInformation.memorandumOfUnderstandingInPlace'
    ),
    1
  );

  const subProgramManagementCostCharges = _.flattenDepth(
    collectCostsWithSubProgram(
      [],
      managementCostFundingStream[0],
      'additionalInformation.narrative.orgIdPerformingWork',
      orgId,
      false,
      'additionalInformation.organizationType',
      'additionalInformation.memorandumOfUnderstandingInPlace'
    ),
    1
  );

  const subProgramPassthroughManagementCostCharges = _.flattenDepth(
    collectCostsWithSubProgram(
      [],
      managementCostFundingStream[0],
      'additionalInformation.narrative.orgIdPerformingWork',
      orgId,
      true,
      'additionalInformation.organizationType',
      'additionalInformation.memorandumOfUnderstandingInPlace'
    ),
    1
  );

  const subProgramRecipientManagementCostTotalCharges = _.flattenDepth(
    collectCostsWithSubProgramAndSolutionArea(
      [],
      matchingFundingStream[0],
      'additionalInformation.orgIdPerformingWork',
      orgId,
      true,
      'additionalInformation.organizationType',
      'additionalInformation.memorandumOfUnderstandingInPlace'
    ),
    1
  );

  const subProgramPassthroughTotals = sumSubProgramTotals(subProgramPassthroughCharges);

  const subProgramInvestmentTotals = sumSubProgramTotals(subProgramTotalCharges);

  const subProgramManagementCostTotals = sumSubProgramTotals(
    subProgramManagementCostCharges
  );

  const subProgramPassthroughManagementCostTotals = sumSubProgramTotals(
    subProgramPassthroughManagementCostCharges
  );

  const subProgramRecipientManagementCostTotals = sumSubProgramTotals(
    subProgramRecipientManagementCostTotalCharges
  );

  let subProgramTotals = {};
  subProgramTotals.totalPassthrough = subProgramPassthroughTotals;
  subProgramTotals.totalInvestments = subProgramInvestmentTotals;
  subProgramTotals.totalManagementCost = subProgramManagementCostTotals;
  subProgramTotals.totalPassthroughManagementCost = subProgramPassthroughManagementCostTotals;
  subProgramTotals.totalRecipientManagementCost = subProgramRecipientManagementCostTotals;

  const solutionAreaTotals = sumSolutionAreaTotals(subProgramTotalCharges);
  return {
    subProgramTotals,
  };
};

export const computeBudgetByInvestments = (fundingStreams, orgId) => {
  const budgetSummary = computeBudgetSummaryByInvestment(fundingStreams, orgId);
  return {
    budgetSummary,
  };
};

const getSubOrgMACost = (item) => {
  let costItems = BudgetUtils.collectItemsContainingFieldPath([], COST, item);
  let subOrgCost = 0;
  _.forEach(costItems, (costItem) => {
    const solutionArea = _.get(costItem, 'additionalInformation.solutionArea');
    if (solutionArea === 'MANAGEMENT_AND_ADMINISTRATION') {
      const costs = _.get(costItem, COST, []);
      _.forEach(costs, (costAry) => {
        let total = _.get(costAry, '0.total');
        if (_.has(costAry, 'actualTotal')) {
          total = _.get(costAry, '0.actualTotal', 0);
        }
        subOrgCost += total;
      });
    }
  });
  return subOrgCost;
};
const collectMgmtCostForSubOrgs = (itemsWithOrgId, parentOrgId) => {
  const subOrgItems = new Map();
  _.forEach(itemsWithOrgId, (item) => {
    const orgId = _.toNumber(_.get(item, PERFORMING_ORG_ID_PATH));
    if (orgId && orgId != parentOrgId) {
      let subOrgCost = getSubOrgMACost(item);
      let existingCost = subOrgItems.get(orgId);
      if (existingCost) subOrgCost = subOrgCost + existingCost;
      subOrgItems.set(orgId, subOrgCost);
    }
  });
  return subOrgItems;
};

const collectSubRecipientCosts = (obj, appOrgId) => {
  const itemsWithOrgId = BudgetUtils.collectItemsContainingFieldPath(
    [],
    PERFORMING_ORG_ID_PATH,
    obj
  );
  const subOrgItems = collectMgmtCostForSubOrgs(itemsWithOrgId, _.toNumber(appOrgId));
  return subOrgItems;
};

export const sumTotalSubRecipientManagementCosts = (
  fundingStreams,
  appOrgId,
  isRecipient,
  orgIdPerformingWork
) => {
  let accumulatedValue = 0;
  _.forEach(fundingStreams, (child) => {
    const children = _.get(child, 'children', {});
    let collectedCosts = new Map(); // Declare the map here
    _.forEach(children, (item) => {
      if (!isRecipient) {
        collectedCosts = collectSubRecipientCosts(item, appOrgId);
      }
    });

    if (collectedCosts.has(_.toNumber(orgIdPerformingWork))) {
      const value = collectedCosts.get(_.toNumber(orgIdPerformingWork));
      accumulatedValue += value;
    }
  });
  return accumulatedValue;
};

export const calculateManagementCostThreshold = ({data, fundingStreams, costshareFS}) => {
  let percentBasedOnManagementCost = _.get(
    costshareFS,
    'managementAdminCosts.allowablePercentBasedOn'
  );
  let maxPercentageOfManagementCost = _.get(
    costshareFS,
    'managementAdminCosts.allowablePercent'
  );
  let managementTotalCost = _.reduce(
    _.get(data.budgetSummary, 'totalManagementCosts'),
    (acc, periodCharges) => safeAdd(acc, periodCharges)
  );

  let totalCost = _.reduce(
    _.get(data.budgetSummary, 'totalCharges'),
    (acc, periodCharges) => safeAdd(acc, periodCharges)
  );
  totalCost = totalCost ? totalCost : 0;

  let totalFedShare = _.reduce(_.get(data.share, 'fedShare'), (acc, periodCharges) =>
    safeAdd(acc, periodCharges)
  );
  totalFedShare = totalFedShare ? totalFedShare : 0;

  let managementCostThreshold;

  switch (percentBasedOnManagementCost) {
    case FED_SHARE_AWARD_COST_NON_MA_AND_MA:
      managementCostThreshold =
        _.toNumber(totalFedShare) * _.toNumber(maxPercentageOfManagementCost) * 0.01;
      break;
    case TOT_AWARD_COST:
    case TOT_PROJECT_COST:
      managementCostThreshold =
        _.toNumber(totalCost) * _.toNumber(maxPercentageOfManagementCost) * 0.01;
      break;
    case FED_SHARE_COST_NON_MA_AND_MA:
      managementCostThreshold = calcTotalsByExcludingManagementCosts({
        fundingStreams,
        skipFS: 'managementCosts',
        maxAllowablePercentage: _.toNumber(maxPercentageOfManagementCost),
        costShareConfig: costshareFS,
      }).mcLimit;
      break;
    case TOT_AWARD_COST_NON_MA_AND_MA:
    default:
      const totalNonManagementCost = totalCost - managementTotalCost;
      managementCostThreshold =
        _.toNumber(totalNonManagementCost) *
        _.toNumber(maxPercentageOfManagementCost) *
        0.01;
  }

  return {
    managementTotalCost: managementTotalCost,
    managementCostThreshold: managementCostThreshold,
  };
};
//*********** END ************** *//
